import { useEffect, useMemo, useState } from 'react';
import { SEARCH_PAUSE_MS, type TaxPreviewAnswer, type TaxPreviewLine, type TaxPreviewRequest } from '@bms2/shared';
import type { ItemLineDraft } from '../../components/composites/ItemTable';
import { apiFetch } from './api';

/** 🔨 Relative to the API's own root: the base URL carries the prefix, this does not (decision 0644). */
export const TAX_PREVIEW_PATH = '/tax/preview';

export interface TaxPreview {
  /** The last answer that landed; it stays standing while a newer one is asked and where one fails. */
  answer: TaxPreviewAnswer | null;
  /** The refusal the last preview met, or null where it answered. */
  failure: string | null;
  /** The tax the last answer gave one row of the form, by the row's own key; null where it gave none. */
  taxCentsOf: (key: string) => number | null;
}

/**
 * 🔒 A sales document's form asks the tax preview on every change to a line,
 * the Ship-To or the date, after the Dropdown's own pause (decision 0644), and
 * draws the answer under each category and in the totals box.
 *
 * 🔨 A request that has not settled is abandoned when the form moves on, so an
 * answer to an older state of the form never lands over a newer one. 🔨 A
 * failed preview leaves the last answer standing and says so; it never blocks
 * the save, which works the tax out again on its own.
 */
export function useTaxPreview(request: TaxPreviewRequest | null, apiBaseUrl = ''): TaxPreview {
  const body = request && request.lines.length > 0 ? JSON.stringify(request) : null;
  const [held, setAnswer] = useState<TaxPreviewAnswer | null>(null);
  const [heldFailure, setFailure] = useState<string | null>(null);
  // A form with no line to ask about has no tax to show, whatever the last answer was.
  const answer = body === null ? null : held;
  const failure = body === null ? null : heldFailure;

  useEffect(() => {
    if (body === null) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response = await apiFetch(`${apiBaseUrl}${TAX_PREVIEW_PATH}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body, signal: controller.signal });
          const payload = (await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as (TaxPreviewAnswer & { message?: string | string[] }) | null;
          if (controller.signal.aborted) return;
          if (!response.ok || !payload) {
            const message = Array.isArray(payload?.message) ? payload.message.join('; ') : payload?.message;
            setFailure(message ?? `the server refused the preview (${response.status})`);
            return;
          }
          setAnswer(payload);
          setFailure(null);
        } catch {
          // silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server.
          if (!controller.signal.aborted) setFailure('the server could not be reached');
        }
      })();
    }, SEARCH_PAUSE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [body, apiBaseUrl]);

  const taxCentsOf = useMemo(() => {
    const byKey = new Map((answer?.lines ?? []).map((line) => [line.key, line.taxCents]));
    return (key: string): number | null => byKey.get(key) ?? null;
  }, [answer]);

  return { answer, failure, taxCentsOf };
}

/**
 * The rows of an Item Table the preview is asked about: every line that names
 * an Item or carries text, keyed by the row's own id so the answer comes back
 * to the row that asked. A divider and the untouched opening row are left out.
 */
export function previewLinesOf(drafts: readonly ItemLineDraft[], extra: (draft: ItemLineDraft) => Partial<TaxPreviewLine> = () => ({})): TaxPreviewLine[] {
  return drafts
    .filter((d) => d.kind !== 'divider' && (d.itemId || d.itemLabel.trim() || d.description.trim()))
    .map((d) => ({
      key: d.id,
      itemId: d.itemId,
      taxCategoryId: d.taxCategoryId ?? null,
      quantity: d.qty ?? 0,
      unitPriceCents: d.rateCents,
      ...extra(d),
    }));
}
